import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, Building2, Car, Ship, MessageCircle, TrendingUp, LogOut, User, Sparkles, Crown, BarChart3, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, Target, Wallet, Compass, ClipboardCheck, Heart, Settings, PieChart, LineChart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Nav from '@/components/Nav';
import LifestyleBundles from '@/components/LifestyleBundles';
import CurrencySelector from '@/components/CurrencySelector';
import PriceDisplay from '@/components/PriceDisplay';
import AINextServices from '@/components/AINextServices';
import QuotationsInbox from '@/components/QuotationsInbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [dreamScore, setDreamScore] = useState(null);
  const [properties, setProperties] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScoreDetails, setShowScoreDetails] = useState(false);
  const [dreamMatch, setDreamMatch] = useState(null);
  const [freeExplore, setFreeExplore] = useState({ cars: [], assets: [] });
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [featureAccess, setFeatureAccess] = useState(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch access control + subscription in parallel
      const [subRes, accessRes] = await Promise.all([
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/bundles/access`).catch(() => ({ data: null })),
      ]);
      setSubscriptionStatus(subRes.data);
      if (accessRes.data) {
        setFeatureAccess(accessRes.data.features);
        setOnboardingComplete(accessRes.data.onboarding_complete);
        setIsFirstTimeUser(!accessRes.data.onboarding_complete);
      }

      // Check onboarding — redirect only for brand-new users
      if (accessRes.data && !accessRes.data.onboarding_complete) {
        // Still load some data but don't redirect — show limited dashboard
        const propsRes = await axios.get(`${API}/properties`);
        setProperties(propsRes.data.slice(0, 3));
        setLoading(false);
        return;
      }

      // Full data load for onboarded users
      const [scoreRes, propsRes] = await Promise.all([
        axios.get(`${API}/dream-match/score`),
        axios.get(`${API}/properties`),
      ]);
      setDreamScore(scoreRes.data);
      setProperties(propsRes.data.slice(0, 3));

      // Fetch Dream Match recommendations (paid feature)
      const access = accessRes.data?.features;
      if (access?.dream_matches || access?.curated_for_you) {
        try {
          const matchRes = await axios.get(`${API}/dream-match/recommendations`);
          setDreamMatch(matchRes.data);
        } catch (err) {
        }
      } else {
        // Free users: fetch a small marketplace preview (3 cars + 3 other assets) so
        // the Free dashboard surfaces all 4 lifestyle categories: Bundles → Properties → Cars → Assets.
        try {
          const mp = await axios.get(`${API}/marketplace`);
          const items = Array.isArray(mp.data) ? mp.data : [];
          const cars = items.filter(i => i.category === 'cars').slice(0, 3);
          const assets = items.filter(i => i.category !== 'cars').slice(0, 3);
          setFreeExplore({ cars, assets });
        } catch (err) {
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Admin gets full access to everything regardless of subscription tier
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || subscriptionStatus?.is_admin === true;
  const tier = isAdmin ? 'elite' : (subscriptionStatus?.tier || user?.subscription_tier || 'free');

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading your dream dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      {/* Navigation */}
      <Nav activePage="/dashboard" tier={tier} isAdmin={isAdmin} />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Welcome Section - Mobile Responsive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 sm:mb-12 lg:mb-16 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4"
        >
          <div>
            <h2 className="font-playfair text-2xl sm:text-3xl lg:text-5xl font-semibold mb-2 sm:mb-4">
              {isFirstTimeUser ? 'Welcome, ' : 'Welcome back, '}<span className="text-gold block sm:inline">{user?.name}</span>
            </h2>
            <p className="text-text-secondary text-sm sm:text-base lg:text-lg">Your smart planning journey {isFirstTimeUser ? 'begins' : 'continues'}</p>
          </div>
          <div className="flex items-center gap-3">
            <CurrencySelector />
            <span className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${
              isAdmin ? 'bg-gradient-to-r from-red-500 to-red-700 text-white' :
              tier === 'elite' ? 'bg-gradient-to-r from-gold to-yellow-600 text-black' :
              tier === 'premium' ? 'bg-gold/20 text-gold border border-gold' :
              'bg-white/10 text-text-secondary'
            }`}>
              <Crown className="w-4 h-4 inline mr-2" />
              {isAdmin ? 'Admin' : tier.charAt(0).toUpperCase() + tier.slice(1)}
            </span>
            {!isAdmin && tier === 'free' && (
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-gold text-black hover:bg-gold-light"
                data-testid="upgrade-btn"
              >
                Upgrade
              </Button>
            )}
            {!isAdmin && tier === 'premium' && (
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-gradient-to-r from-gold to-yellow-600 text-black hover:opacity-90"
                data-testid="upgrade-to-elite-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" /> Upgrade to Elite
              </Button>
            )}
          </div>
        </motion.div>

        {/* Free Tier Upgrade Banner */}
        {!isAdmin && tier === 'free' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 mb-8 border border-gold/30 bg-gradient-to-r from-gold/10 to-transparent"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Crown className="w-10 h-10 text-gold" />
                <div>
                  <h3 className="font-semibold text-lg">Unlock Premium Features</h3>
                  <p className="text-text-secondary">Get unlimited property views, full AI concierge access, and investor tools</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-gold text-black hover:bg-gold-light"
              >
                View Plans
              </Button>
            </div>
          </motion.div>
        )}

        {/* Dream Readiness Score - Shown for ALL users who completed onboarding */}
        {onboardingComplete && dreamScore && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-8 md:p-12 mb-12 relative overflow-hidden"
          data-testid="dream-score-card"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />
          
          {/* Main Score Header */}
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gold/20 flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-2xl md:text-3xl font-semibold">ASTIIV BASGAT Readiness Score</h3>
                    <p className="text-text-secondary text-sm">{dreamScore?.tier_message || 'Complete your profile to unlock your score'}</p>
                  </div>
                </div>
              </div>
              
              {/* Score Circle */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" className="text-white/10" />
                    <circle 
                      cx="64" cy="64" r="56" 
                      stroke="url(#scoreGradient)" 
                      strokeWidth="8" 
                      fill="none" 
                      strokeLinecap="round"
                      strokeDasharray={`${(dreamScore?.readiness_score || 0) * 3.52} 352`}
                      className="transition-all duration-1000"
                    />
                    <defs>
                      <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#D4AF37" />
                        <stop offset="100%" stopColor="#F4D03F" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-playfair text-4xl font-bold text-gold">{dreamScore?.readiness_score || 0}</span>
                    <span className="text-text-secondary text-sm">/100</span>
                  </div>
                </div>
                <div className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize ${
                  dreamScore?.tier === 'platinum' ? 'bg-gradient-to-r from-purple-500 to-purple-700 text-white' :
                  dreamScore?.tier === 'gold' ? 'bg-gradient-to-r from-gold to-yellow-600 text-black' :
                  dreamScore?.tier === 'silver' ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-black' :
                  dreamScore?.tier === 'bronze' ? 'bg-gradient-to-r from-amber-600 to-amber-800 text-white' :
                  'bg-white/10 text-text-secondary'
                }`}>
                  {dreamScore?.tier || 'Starter'} Status
                </div>
              </div>
            </div>

            {/* Score Explanation Summary */}
            {dreamScore?.explanation?.summary && (
              <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
                <p className="text-text-primary leading-relaxed">{dreamScore.explanation.summary}</p>
              </div>
            )}

            {/* Toggle Details Button */}
            <button 
              onClick={() => setShowScoreDetails(!showScoreDetails)}
              className="flex items-center gap-2 text-gold hover:text-gold-light transition-colors mb-6"
              data-testid="toggle-score-details-btn"
            >
              {showScoreDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              <span className="font-medium">{showScoreDetails ? 'Hide Score Breakdown' : 'View Score Breakdown'}</span>
            </button>

            {/* Detailed Breakdown - Expandable */}
            <AnimatePresence>
              {showScoreDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  {/* Score Categories */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {dreamScore?.breakdown && Object.entries(dreamScore.breakdown).map(([key, data]) => {
                      const icons = {
                        financial: <Wallet className="w-5 h-5" />,
                        home_preferences: <Building2 className="w-5 h-5" />,
                        lifestyle: <Compass className="w-5 h-5" />,
                        completeness: <ClipboardCheck className="w-5 h-5" />
                      };
                      const percentage = (data.score / data.max) * 100;
                      
                      return (
                        <div key={key} className="bg-white/5 rounded-lg p-4 border border-white/10" data-testid={`breakdown-${key}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="text-gold">{icons[key] || <Target className="w-5 h-5" />}</div>
                              <span className="font-medium">{data.label}</span>
                            </div>
                            <span className="text-gold font-bold">{data.score}/{data.max}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                            <motion.div 
                              className="h-full bg-gradient-to-r from-gold to-yellow-500 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{ duration: 0.8, delay: 0.2 }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Positive Factors */}
                  {dreamScore?.explanation?.factors && dreamScore.explanation.factors.length > 0 && (
                    <div className="mb-8">
                      <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                        What's Boosting Your Score
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {dreamScore.explanation.factors.map((factor, idx) => (
                          <div key={`factor-${idx}`} className="bg-green-500/10 border border-green-500/20 rounded-lg p-4" data-testid={`factor-${idx}`}>
                            <div className="flex items-start justify-between mb-2">
                              <span className="font-medium text-green-400">{factor.name}</span>
                              <span className="text-green-400 font-bold text-sm">+{factor.points} pts</span>
                            </div>
                            <p className="text-text-secondary text-sm">{factor.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {dreamScore?.explanation?.recommendations && dreamScore.explanation.recommendations.length > 0 && (
                    <div className="mb-8">
                      <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-400" />
                        Recommendations to Improve Your Score
                      </h4>
                      <div className="space-y-3">
                        {dreamScore.explanation.recommendations.map((rec, idx) => (
                          <div key={`rec-${idx}`} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3" data-testid={`recommendation-${idx}`}>
                            <Target className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <p className="text-text-secondary">{rec}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Profile Completeness */}
                  {dreamScore?.explanation?.completeness_percentage !== undefined && (
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-text-secondary">Profile Completeness</span>
                        <span className="text-gold font-bold">{dreamScore.explanation.completeness_percentage}%</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-gold to-yellow-500 rounded-full transition-all duration-500"
                          style={{ width: `${dreamScore.explanation.completeness_percentage}%` }}
                        />
                      </div>
                      {dreamScore.explanation.completeness_percentage < 80 && (
                        <Button 
                          variant="link" 
                          className="text-gold p-0 mt-2 h-auto" 
                          onClick={() => navigate('/onboarding')}
                          data-testid="update-profile-btn"
                        >
                          Update Your Profile →
                        </Button>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 mt-6">
              <Button className="bg-gold text-black hover:bg-gold-light" onClick={() => navigate('/properties')} data-testid="explore-properties-btn">
                <Building2 className="mr-2 h-4 w-4" /> Explore {dreamScore?.matched_properties || 0} Matched Properties
              </Button>
              <Button variant="outline" className="border-white/20 hover:border-gold" onClick={() => navigate('/concierge')} data-testid="talk-concierge-btn">
                <MessageCircle className="mr-2 h-4 w-4" /> Talk to Concierge
              </Button>
              {dreamScore?.readiness_score < 50 && (
                <Button variant="outline" className="border-gold/50 text-gold hover:bg-gold/10" onClick={() => navigate('/onboarding')} data-testid="complete-profile-btn">
                  <Sparkles className="mr-2 h-4 w-4" /> Complete Profile
                </Button>
              )}
            </div>
          </div>
        </motion.div>
        )}

        {/* Onboarding CTA for users who haven't completed profile */}
        {!onboardingComplete && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="glass-card p-8 md:p-12 mb-12 relative overflow-hidden border border-gold/20 bg-gradient-to-r from-gold/5 to-purple-500/5">
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gold/20 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-gold" />
              </div>
              <h3 className="font-playfair text-2xl md:text-3xl font-semibold mb-4">Complete Your Profile</h3>
              <p className="text-text-secondary text-lg mb-6 max-w-xl mx-auto">
                Unlock AI Readiness Score, Dream Matches, personalized bundles, and curated recommendations by completing your profile.
              </p>
              <Button onClick={() => navigate('/onboarding')} className="bg-gold text-black hover:bg-gold-light px-8 py-6 text-lg" data-testid="onboarding-cta-btn">
                <Sparkles className="mr-2 h-5 w-5" /> Start Profile
              </Button>
            </div>
          </motion.div>
        )}

        {/* ──────────────────────────────────────────────────────────
            DASHBOARD ORDER (post-onboarding):
            1. Readiness Score (above)
            2. AI Insights (Score breakdown — collapsible above)
            3. Bundles (PRIMARY)  ← here
            4. Properties (matched for Premium/Elite, top 3 for Free)
            5. Cars (matched for Premium/Elite, top 3 for Free)
            6. Other Assets (matched for Premium/Elite, top 3 for Free)
            7. Quick Stats / Portfolio / Lifestyle Categories (below)
           ────────────────────────────────────────────────────────── */}

        {/* 3. Lifestyle Bundles — PRIMARY focus, moved above all asset rails */}
        {onboardingComplete && (featureAccess?.ai_bundles || isAdmin) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-16"
          data-testid="dashboard-bundles-section"
        >
          <LifestyleBundles tier={tier} isAdmin={isAdmin} onboardingComplete={onboardingComplete} />
        </motion.div>
        )}

        {/* 3b. Quotations Inbox — Phase 5 (renders only if user has quotations) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.13 }}
          className="mb-12"
        >
          <QuotationsInbox />
        </motion.div>

        {/* 3c. AI Closed-Loop Recommendations — Phase 6 */}
        {onboardingComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="mb-12"
          >
            <AINextServices />
          </motion.div>
        )}

        {/* 4. Matched Assets Section - paid users + admin */}
        {onboardingComplete && (featureAccess?.dream_matches || isAdmin) && dreamScore?.matched_assets?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }} className="mb-12">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gold" />
              <span>Properties Within Your Budget</span>
              <span className="text-sm text-gold font-normal ml-2">({dreamScore.matched_properties} total)</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dreamScore.matched_assets.slice(0, 6).map((asset) => (
                <div key={asset.id} className="glass-card p-4 hover:border-gold/30 transition-all cursor-pointer group" onClick={() => navigate(`/properties/${asset.id}`)} data-testid={`matched-asset-${asset.id}`}>
                  <div className="aspect-[16/9] overflow-hidden rounded mb-3 bg-white/5">
                    <img src={asset.image || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400'} alt={asset.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400'; }} />
                  </div>
                  <h4 className="font-medium text-sm mb-1 truncate">{asset.title}</h4>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-text-secondary">{asset.location}</span>
                    <PriceDisplay amount={asset.price} className="text-gold font-bold" />
                  </div>
                  {(asset.bedrooms || asset.type) && (
                    <div className="flex gap-3 mt-2 text-xs text-text-secondary">
                      {asset.bedrooms && <span>{asset.bedrooms} BR</span>}
                      {asset.type && <span className="capitalize">{asset.type}</span>}
                      {asset.built_area && <span>{asset.built_area.toLocaleString()} sqft</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {dreamScore.matched_properties > 6 && (
              <Button variant="outline" className="mt-4 border-gold/30 text-gold hover:bg-gold/10" onClick={() => navigate('/properties')} data-testid="view-all-matched-btn">
                View All {dreamScore.matched_properties} Matched Properties
              </Button>
            )}
          </motion.div>
        )}

        {/* Nearest Recommendations (when no exact budget matches) - paid + onboarded */}
        {onboardingComplete && (featureAccess?.dream_matches || isAdmin) && dreamScore?.nearest_assets?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="mb-12">
            <div className="glass-card p-6 border-yellow-500/20 bg-yellow-500/5 mb-4">
              <p className="text-yellow-400 text-sm font-medium">{dreamScore.no_match_message || 'No exact matches found. Here are the nearest options:'}</p>
            </div>
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold" />
              <span>Recommended Properties (Nearest to Budget)</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dreamScore.nearest_assets.map((asset) => (
                <div key={asset.id} className="glass-card p-4 hover:border-gold/30 transition-all cursor-pointer group" onClick={() => navigate(`/properties/${asset.id}`)} data-testid={`nearest-asset-${asset.id}`}>
                  <div className="aspect-[16/9] overflow-hidden rounded mb-3 bg-white/5">
                    <img src={asset.image || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400'} alt={asset.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400'; }} />
                  </div>
                  <h4 className="font-medium text-sm mb-1 truncate">{asset.title}</h4>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-text-secondary">{asset.location}</span>
                    <PriceDisplay amount={asset.price} className="text-gold font-bold" />
                  </div>
                  {(asset.bedrooms || asset.type) && (
                    <div className="flex gap-3 mt-2 text-xs text-text-secondary">
                      {asset.bedrooms && <span>{asset.bedrooms} BR</span>}
                      {asset.type && <span className="capitalize">{asset.type}</span>}
                      {asset.built_area && <span>{asset.built_area.toLocaleString()} sqft</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Lifestyle Bundles — moved above (see "3. Lifestyle Bundles" block) */}

        {/* 5+6. Free-tier Explore (Cars + Other Assets) — non-personalised preview so Free
             users see all 4 lifestyle categories (Bundles → Properties → Cars → Assets). */}
        {onboardingComplete && !(featureAccess?.dream_matches || isAdmin) && (freeExplore.cars.length > 0 || freeExplore.assets.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="mb-16"
            data-testid="free-explore-section"
          >
            {freeExplore.cars.length > 0 && (
              <div className="mb-10">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-gold" />
                    <h3 className="font-playfair text-2xl font-semibold">Explore Luxury Cars</h3>
                  </div>
                  <Button variant="ghost" className="text-gold" onClick={() => navigate('/marketplace?category=cars')} data-testid="free-explore-cars-all">
                    View All →
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {freeExplore.cars.map((car, idx) => (
                    <div key={car.id || idx} className="glass-card overflow-hidden cursor-pointer hover:border-gold/30 transition-all"
                         onClick={() => navigate(`/marketplace/${car.id}?category=cars`)}
                         data-testid={`free-explore-car-${idx}`}>
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={car.images?.[0] || 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600'} alt={car.title || car.model} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4">
                        <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">{car.brand}</div>
                        <h4 className="font-semibold text-lg mb-2">{car.title || car.model}</h4>
                        <PriceDisplay amount={car.price} className="text-gold font-bold" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {freeExplore.assets.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-gold" />
                    <h3 className="font-playfair text-2xl font-semibold">Explore Lifestyle Assets</h3>
                  </div>
                  <Button variant="ghost" className="text-gold" onClick={() => navigate('/marketplace')} data-testid="free-explore-assets-all">
                    View All →
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {freeExplore.assets.map((item, idx) => (
                    <div key={item.id || idx} className="glass-card overflow-hidden cursor-pointer hover:border-gold/30 transition-all"
                         onClick={() => navigate(`/marketplace/${item.id}?category=${item.category}`)}
                         data-testid={`free-explore-asset-${idx}`}>
                      <div className="aspect-[16/10] overflow-hidden relative">
                        <img src={item.images?.[0] || 'https://images.unsplash.com/photo-1614164185128-e4ec99c436d7?w=600'} alt={item.title || item.model} className="w-full h-full object-cover" />
                        <div className="absolute top-3 left-3 bg-white/10 backdrop-blur-sm text-white px-2 py-1 rounded text-xs capitalize">
                          {item.category}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">{item.brand}</div>
                        <h4 className="font-semibold text-lg mb-2">{item.title || item.model}</h4>
                        <PriceDisplay amount={item.price} className="text-gold font-bold" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 glass-card p-4 border-gold/20 bg-gold/5 text-center text-sm text-text-secondary" data-testid="free-upgrade-hint">
                  Upgrade to <span className="text-gold font-semibold">Premium</span> for personalised matches across cars, watches, yachts and more.
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-8 hover:bg-white/10 transition-all"
            data-testid="matched-properties-card"
          >
            <Building2 className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{dreamScore?.matched_properties || 0}</div>
            <div className="text-text-secondary">Matched Properties</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-8 hover:bg-white/10 transition-all"
            data-testid="subscription-card"
          >
            <Crown className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2 capitalize">{tier}</div>
            <div className="text-text-secondary">Subscription Tier</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className={`glass-card p-8 transition-all ${
              (isAdmin || tier === 'premium' || tier === 'elite') ? 'hover:bg-white/10 cursor-pointer' : 'opacity-60'
            }`}
            onClick={() => (isAdmin || tier === 'premium' || tier === 'elite') ? navigate('/investor') : toast.info('Upgrade to Premium to access Investor Mode')}
            data-testid="investor-mode-card"
          >
            <TrendingUp className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">
              {(isAdmin || tier === 'premium' || tier === 'elite') ? 'Active' : 'Locked'}
            </div>
            <div className="text-text-secondary">Investor Mode</div>
          </motion.div>
        </div>

        {/* Portfolio Insights Banner - Premium/Elite Only */}
        {(isAdmin || tier === 'premium' || tier === 'elite') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mb-12"
          >
            <div 
              onClick={() => navigate('/investor/insights')}
              className="bg-gradient-to-r from-gold/10 via-purple-500/10 to-blue-500/10 border border-gold/20 rounded-2xl p-6 cursor-pointer hover:border-gold/40 transition-all group"
              data-testid="portfolio-insights-banner"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-gold/30 to-purple-500/30 rounded-xl flex items-center justify-center">
                    <PieChart className="w-8 h-8 text-gold" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-playfair text-xl font-semibold">Portfolio Insights Dashboard</h3>
                      <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" /> {tier === 'elite' ? 'Elite' : 'Premium'}
                      </span>
                    </div>
                    <p className="text-text-secondary">Track asset growth, investment diversification, lifestyle score & ROI projections</p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2 text-text-secondary">
                      <LineChart className="w-4 h-4 text-emerald-400" />
                      <span>Value Growth</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <PieChart className="w-4 h-4 text-purple-400" />
                      <span>Diversification</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <Target className="w-4 h-4 text-blue-400" />
                      <span>Lifestyle Score</span>
                    </div>
                  </div>
                  <Button className="bg-gold text-black hover:bg-gold-light group-hover:scale-105 transition-transform">
                    Open Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Dream Match Section - Your Personalized Recommendations (Paid + Onboarded only) */}
        {onboardingComplete && (featureAccess?.dream_matches || isAdmin) && dreamMatch && (dreamMatch.cars?.length > 0 || dreamMatch.products?.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mb-16"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold/30 to-purple-500/30 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-playfair text-2xl font-semibold">Your Dream Match</h3>
                  <p className="text-text-secondary text-sm">AI-curated recommendations based on your profile</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                className="text-gold" 
                onClick={() => navigate('/marketplace')}
                data-testid="view-all-marketplace-btn"
              >
                View Marketplace →
              </Button>
            </div>
            
            {/* Cars Section */}
            {dreamMatch.cars?.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Car className="w-5 h-5 text-gold" />
                  <span className="text-lg font-semibold">Recommended Cars</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {dreamMatch.cars.map((car, index) => (
                    <motion.div
                      key={car.id || index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="group glass-card overflow-hidden cursor-pointer hover:border-gold/30 transition-all"
                      onClick={() => navigate(`/marketplace/${car.id}?category=cars`)}
                      data-testid={`dream-match-car-${index}`}
                    >
                      <div className="aspect-[16/10] overflow-hidden relative">
                        <img 
                          src={car.images?.[0] || 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600'}
                          alt={car.title || car.model}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-gold text-black px-2 py-1 rounded text-xs font-bold">
                          {car.luxury_tier?.toUpperCase() || 'LUXURY'}
                        </div>
                        {car.dreamlife_score && (
                          <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm px-2 py-1 rounded flex items-center gap-1">
                            <span className="text-gold text-xs">⭐ {car.dreamlife_score}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">{car.brand}</div>
                        <h4 className="font-semibold text-lg mb-2">{car.title || car.model}</h4>
                        <PriceDisplay amount={car.price} className="text-gold font-bold" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Other Products Section */}
            {dreamMatch.products?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-gold" />
                  <span className="text-lg font-semibold">Curated For You</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {dreamMatch.products.map((product, index) => (
                    <motion.div
                      key={product.id || index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      className="group glass-card overflow-hidden cursor-pointer hover:border-gold/30 transition-all"
                      onClick={() => navigate(`/marketplace/${product.id}?category=${product.category}`)}
                      data-testid={`dream-match-product-${index}`}
                    >
                      <div className="aspect-[16/10] overflow-hidden relative">
                        <img 
                          src={product.images?.[0] || 'https://images.unsplash.com/photo-1614164185128-e4ec99c436d7?w=600'}
                          alt={product.title || product.model}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 bg-white/10 backdrop-blur-sm text-white px-2 py-1 rounded text-xs capitalize">
                          {product.category}
                        </div>
                        {product.dreamlife_score && (
                          <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm px-2 py-1 rounded flex items-center gap-1">
                            <span className="text-gold text-xs">⭐ {product.dreamlife_score}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">{product.brand}</div>
                        <h4 className="font-semibold text-lg mb-2">{product.title || product.model}</h4>
                        <PriceDisplay amount={product.price} className="text-gold font-bold" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Featured Properties — "Your Dream Matches" (hidden before onboarding for free) */}
        {onboardingComplete && (featureAccess?.dream_matches || isAdmin) ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex justify-between items-center mb-8">
            <h3 className="font-playfair text-3xl font-semibold">Your Dream Matches</h3>
            <Button variant="ghost" className="text-gold" onClick={() => navigate('/properties')} data-testid="view-all-properties-btn">
              View All →
            </Button>
          </div>

          {properties.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <p className="text-text-secondary text-lg mb-6">No properties available yet. Check back soon!</p>
              <Button className="bg-gold text-black hover:bg-gold-light" onClick={() => navigate('/concierge')} data-testid="contact-concierge-btn">
                Contact Concierge
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {properties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="group relative overflow-hidden bg-neutral-900/50 border border-white/5 hover:border-gold/30 transition-all duration-500 cursor-pointer"
                  onClick={() => navigate(`/properties/${property.id}`)}
                  data-testid={`property-card-${index}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={property.images[0] || 'https://images.unsplash.com/photo-1606917454663-343e58811bb1?w=800'}
                      alt={property.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="font-playfair text-xl font-semibold mb-2">{property.title}</h4>
                    <p className="text-text-secondary mb-4">{property.location}</p>
                    <div className="flex justify-between items-center">
                      <PriceDisplay amount={property.price} className="text-gold text-2xl font-bold" />
                      <span className="text-text-secondary text-sm">{property.bedrooms} BR</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
        ) : (
          /* Show "Explore Properties" for non-onboarded or free users */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <div className="flex justify-between items-center mb-8">
              <h3 className="font-playfair text-3xl font-semibold">Explore Properties</h3>
              <Button variant="ghost" className="text-gold" onClick={() => navigate('/properties')} data-testid="view-all-properties-btn">
                View All →
              </Button>
            </div>
            {properties.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <p className="text-text-secondary text-lg mb-6">No properties available yet. Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {properties.map((property, index) => (
                  <motion.div key={property.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="group relative overflow-hidden bg-neutral-900/50 border border-white/5 hover:border-gold/30 transition-all duration-500 cursor-pointer"
                    onClick={() => navigate(`/properties/${property.id}`)} data-testid={`property-card-${index}`}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={property.images?.[0] || 'https://images.unsplash.com/photo-1606917454663-343e58811bb1?w=800'} alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700" />
                    </div>
                    <div className="p-6">
                      <h4 className="font-playfair text-xl font-semibold mb-2">{property.title}</h4>
                      <p className="text-text-secondary mb-4">{property.location}</p>
                      <div className="flex justify-between items-center">
                        <PriceDisplay amount={property.price} className="text-gold text-2xl font-bold" />
                        <span className="text-text-secondary text-sm">{property.bedrooms} BR</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Lifestyle Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-16"
        >
          <h3 className="font-playfair text-3xl font-semibold mb-8">Complete Your ASTIIV BASGATstyle</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="glass-card p-8 text-center hover:bg-white/10 transition-all cursor-pointer" onClick={() => navigate('/marketplace?category=cars')} data-testid="category-cars">
              <Car className="w-12 h-12 text-gold mx-auto mb-4" />
              <div className="font-semibold">Luxury Cars</div>
            </div>
            <div className="glass-card p-8 text-center hover:bg-white/10 transition-all cursor-pointer" onClick={() => navigate('/marketplace?category=yachts')} data-testid="category-yachts">
              <Ship className="w-12 h-12 text-gold mx-auto mb-4" />
              <div className="font-semibold">Yachts</div>
            </div>
            <div className="glass-card p-8 text-center hover:bg-white/10 transition-all cursor-pointer" onClick={() => navigate('/marketplace?category=interiors')} data-testid="category-interiors">
              <Home className="w-12 h-12 text-gold mx-auto mb-4" />
              <div className="font-semibold">Interiors</div>
            </div>
            <div className="glass-card p-8 text-center hover:bg-white/10 transition-all cursor-pointer" onClick={() => navigate('/marketplace')} data-testid="category-more">
              <Sparkles className="w-12 h-12 text-gold mx-auto mb-4" />
              <div className="font-semibold">& More</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
